import type {
  AgentRuntimeInteractionRequest,
  AgentRuntimeQueuedMessage,
  AgentRuntimeSnapshot,
  AgentRuntimeTimelineItem,
} from "@cord/shared"

function countDefinedOwnKeys(value: Record<string, unknown>): number {
  let count = 0
  for (const key in value) {
    if (!Object.prototype.hasOwnProperty.call(value, key)) {
      continue
    }

    if (value[key] !== undefined) {
      count += 1
    }
  }

  return count
}

function areRuntimeValuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) {
    return true
  }

  if (typeof left !== typeof right) {
    return false
  }

  if (
    left === null ||
    right === null ||
    typeof left !== "object" ||
    typeof right !== "object"
  ) {
    return false
  }

  const leftObject = left as Record<string, unknown>
  const rightObject = right as Record<string, unknown>

  if (Array.isArray(leftObject) || Array.isArray(rightObject)) {
    if (!Array.isArray(leftObject) || !Array.isArray(rightObject)) {
      return false
    }

    if (leftObject.length !== rightObject.length) {
      return false
    }

    for (let index = 0; index < leftObject.length; index += 1) {
      if (!areRuntimeValuesEqual(leftObject[index], rightObject[index])) {
        return false
      }
    }

    return true
  }

  const leftPrototype = Object.getPrototypeOf(leftObject)
  const rightPrototype = Object.getPrototypeOf(rightObject)
  if (leftPrototype !== rightPrototype) {
    return false
  }

  if (leftPrototype !== Object.prototype && leftPrototype !== null) {
    return false
  }

  /**
   * Runtime snapshots can contain large timeline/status objects, so avoid
   * allocating filtered key arrays for every nested plain-object comparison.
   * Missing optional fields and explicit `undefined` must still compare equal.
   */
  if (countDefinedOwnKeys(leftObject) !== countDefinedOwnKeys(rightObject)) {
    return false
  }

  for (const key in leftObject) {
    if (!Object.prototype.hasOwnProperty.call(leftObject, key)) {
      continue
    }

    const leftValue = leftObject[key]
    if (leftValue === undefined) {
      continue
    }

    if (!Object.prototype.hasOwnProperty.call(rightObject, key)) {
      return false
    }

    const rightValue = rightObject[key]
    if (rightValue === undefined) {
      return false
    }

    if (!areRuntimeValuesEqual(leftValue, rightValue)) {
      return false
    }
  }

  return true
}

function sameTimelineItem(left: AgentRuntimeTimelineItem, right: AgentRuntimeTimelineItem): boolean {
  return areRuntimeValuesEqual(left, right)
}

function sameQueuedMessage(left: AgentRuntimeQueuedMessage, right: AgentRuntimeQueuedMessage): boolean {
  return areRuntimeValuesEqual(left, right)
}

function sameInteractionRequest(
  left: AgentRuntimeInteractionRequest,
  right: AgentRuntimeInteractionRequest,
): boolean {
  return areRuntimeValuesEqual(left, right)
}

function reconcileTimeline(
  previous: AgentRuntimeTimelineItem[],
  next: AgentRuntimeTimelineItem[],
): AgentRuntimeTimelineItem[] {
  if (previous.length === 0 || next.length === 0) return next
  if (previous.length === next.length) {
    let changed = false
    const reconciled: AgentRuntimeTimelineItem[] = []
    for (let index = 0; index < next.length; index += 1) {
      const previousItem = previous[index]
      const nextItem = next[index]
      if (!previousItem || previousItem.id !== nextItem.id) {
        changed = true
        break
      }
      if (!sameTimelineItem(previousItem, nextItem)) {
        changed = true
        reconciled.push(nextItem)
        continue
      }
      reconciled.push(previousItem)
    }
    if (!changed || reconciled.length === next.length) {
      return changed ? reconciled : previous
    }
  }
  const previousById = new Map<string, AgentRuntimeTimelineItem>()
  for (const item of previous) {
    previousById.set(item.id, item)
  }
  let changed = previous.length !== next.length
  const reconciled = next.map((item, index) => {
    const previousItem = previousById.get(item.id)
    if (!previousItem || !sameTimelineItem(previousItem, item)) {
      changed = true
      return item
    }
    if (previous[index] !== previousItem) changed = true
    return previousItem
  })
  return changed ? reconciled : previous
}

function sameTimeline(
  left: AgentRuntimeTimelineItem[],
  right: AgentRuntimeTimelineItem[],
): boolean {
  if (left === right) return true
  if (left.length !== right.length) return false
  for (let index = 0; index < left.length; index += 1) {
    const leftItem = left[index]
    const rightItem = right[index]
    if (!leftItem || !rightItem || !sameTimelineItem(leftItem, rightItem)) {
      return false
    }
  }
  return true
}

function reconcileQueuedMessages(
  previous: AgentRuntimeQueuedMessage[],
  next: AgentRuntimeQueuedMessage[],
): AgentRuntimeQueuedMessage[] {
  if (previous.length === 0 || next.length === 0) return next
  if (previous.length === next.length) {
    let changed = false
    const reconciled: AgentRuntimeQueuedMessage[] = []
    for (let index = 0; index < next.length; index += 1) {
      const previousMessage = previous[index]
      const nextMessage = next[index]
      const previousKey = previousMessage?.clientMessageId ?? `${previousMessage?.id ?? previousMessage?.timestamp}:${index}`
      const nextKey = nextMessage.clientMessageId ?? `${nextMessage.id ?? nextMessage.timestamp}:${index}`
      if (!previousMessage || previousKey !== nextKey) {
        changed = true
        break
      }
      if (!sameQueuedMessage(previousMessage, nextMessage)) {
        changed = true
        reconciled.push(nextMessage)
        continue
      }
      reconciled.push(previousMessage)
    }
    if (!changed || reconciled.length === next.length) {
      return changed ? reconciled : previous
    }
  }
  const previousByKey = new Map<string, AgentRuntimeQueuedMessage>()
  for (let index = 0; index < previous.length; index += 1) {
    const message = previous[index]
    if (!message) {
      continue
    }

    previousByKey.set(
      message.clientMessageId ?? `${message.id ?? message.timestamp}:${index}`,
      message,
    )
  }
  let changed = previous.length !== next.length
  const reconciled = next.map((message, index) => {
    const key = message.clientMessageId ?? `${message.id ?? message.timestamp}:${index}`
    const previousMessage = previousByKey.get(key)
    if (!previousMessage || !sameQueuedMessage(previousMessage, message)) {
      changed = true
      return message
    }
    if (previous[index] !== previousMessage) changed = true
    return previousMessage
  })
  return changed ? reconciled : previous
}

function sameQueuedMessages(
  left: AgentRuntimeQueuedMessage[],
  right: AgentRuntimeQueuedMessage[],
): boolean {
  if (left === right) return true
  if (left.length !== right.length) return false
  for (let index = 0; index < left.length; index += 1) {
    const leftMessage = left[index]
    const rightMessage = right[index]
    if (!leftMessage || !rightMessage || !sameQueuedMessage(leftMessage, rightMessage)) {
      return false
    }
  }
  return true
}

function reconcilePendingInteractions(
  previous: AgentRuntimeInteractionRequest[],
  next: AgentRuntimeInteractionRequest[],
): AgentRuntimeInteractionRequest[] {
  if (previous.length === 0 || next.length === 0) return next
  if (previous.length === next.length) {
    let changed = false
    const reconciled: AgentRuntimeInteractionRequest[] = []
    for (let index = 0; index < next.length; index += 1) {
      const previousInteraction = previous[index]
      const nextInteraction = next[index]
      if (
        !previousInteraction ||
        `${previousInteraction.kind}:${previousInteraction.requestId}`
          !== `${nextInteraction.kind}:${nextInteraction.requestId}`
      ) {
        changed = true
        break
      }
      if (!sameInteractionRequest(previousInteraction, nextInteraction)) {
        changed = true
        reconciled.push(nextInteraction)
        continue
      }
      reconciled.push(previousInteraction)
    }
    if (!changed || reconciled.length === next.length) {
      return changed ? reconciled : previous
    }
  }
  const previousByKey = new Map<string, AgentRuntimeInteractionRequest>()
  for (const interaction of previous) {
    previousByKey.set(`${interaction.kind}:${interaction.requestId}`, interaction)
  }
  let changed = previous.length !== next.length
  const reconciled = next.map((interaction, index) => {
    const previousInteraction = previousByKey.get(`${interaction.kind}:${interaction.requestId}`)
    if (!previousInteraction || !sameInteractionRequest(previousInteraction, interaction)) {
      changed = true
      return interaction
    }
    if (previous[index] !== previousInteraction) changed = true
    return previousInteraction
  })
  return changed ? reconciled : previous
}

function samePendingInteractions(
  left: AgentRuntimeInteractionRequest[],
  right: AgentRuntimeInteractionRequest[],
): boolean {
  if (left === right) return true
  if (left.length !== right.length) return false
  for (let index = 0; index < left.length; index += 1) {
    const leftInteraction = left[index]
    const rightInteraction = right[index]
    if (
      !leftInteraction ||
      !rightInteraction ||
      !sameInteractionRequest(leftInteraction, rightInteraction)
    ) {
      return false
    }
  }
  return true
}

export function areRuntimeSnapshotsEquivalent(args: {
  left: AgentRuntimeSnapshot | null
  right: AgentRuntimeSnapshot | null
}): boolean {
  return areRuntimeValuesEqual(args.left, args.right)
}

export function reconcileRuntimeSnapshot(
  previous: AgentRuntimeSnapshot | null,
  next: AgentRuntimeSnapshot,
): AgentRuntimeSnapshot {
  if (!previous) return next

  const timeline = reconcileTimeline(previous.timeline, next.timeline)
  const pendingMessages = reconcileQueuedMessages(previous.pendingMessages, next.pendingMessages)
  const pendingInteractions = reconcilePendingInteractions(
    previous.pendingInteractions,
    next.pendingInteractions,
  )

  const status =
    areRuntimeValuesEqual(previous.status, next.status)
      ? previous.status
      : next.status

  const usage =
    areRuntimeValuesEqual(previous.usage, next.usage)
      ? previous.usage
      : next.usage

  const actions =
    areRuntimeValuesEqual(previous.actions, next.actions)
      ? previous.actions
      : next.actions

  const capabilities =
    areRuntimeValuesEqual(previous.capabilities, next.capabilities)
      ? previous.capabilities
      : next.capabilities

  const cursorState =
    areRuntimeValuesEqual(previous.cursorState, next.cursorState)
      ? previous.cursorState
      : next.cursorState

  const sessionActionOutcome = areRuntimeValuesEqual(
    previous.sessionActionOutcome,
    next.sessionActionOutcome,
  )
    ? previous.sessionActionOutcome
    : next.sessionActionOutcome
  const activeEditOperation = areRuntimeValuesEqual(
    previous.activeEditOperation,
    next.activeEditOperation,
  )
    ? previous.activeEditOperation
    : next.activeEditOperation

  if (
    timeline === previous.timeline &&
    pendingMessages === previous.pendingMessages &&
    pendingInteractions === previous.pendingInteractions &&
    status === previous.status &&
    usage === previous.usage &&
    actions === previous.actions &&
    activeEditOperation === previous.activeEditOperation &&
    previous.conversationEpoch === next.conversationEpoch &&
    sessionActionOutcome === previous.sessionActionOutcome &&
    capabilities === previous.capabilities &&
    cursorState === previous.cursorState
  ) {
    return previous
  }

  return {
    ...next,
    timeline,
    pendingMessages,
    pendingInteractions,
    status,
    usage,
    actions,
    activeEditOperation,
    sessionActionOutcome,
    capabilities,
    cursorState,
  }
}
